import { useState } from "react";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Building2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ApplyDialog } from "@/components/CompanyBits";

export default function Suggestions() {
  const { user, lang } = useAuth();
  const nav = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applyCo, setApplyCo] = useState(null);

  const analyze = async () => {
    if (!user) { nav("/login"); return; }
    if (!user.diploma) { toast.error(t(lang, "fill_diploma_in_profile")); nav("/profile"); return; }
    setLoading(true);
    try {
      const { data } = await http.post("/match/analyze", {
        diploma: user.diploma,
        specialty: user.specialty || "",
        skills: user.skills || [],
        country: user.home_country || null,
        region: user.home_region || null,
      });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="rounded-3xl bg-gradient-to-br from-[#1E3A8A] to-[#172554] text-white p-8 md:p-10 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-[#F97316]/20 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <div className="ai-shimmer w-8 h-8 rounded-full flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-xs uppercase tracking-[0.2em] text-white/80">Gemini 3 Flash · AI Matching</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{t(lang, "suggestions_title")}</h1>
            <p className="mt-3 text-white/80 max-w-2xl">{t(lang, "suggestions_sub")}</p>
            <Button data-testid="analyze-btn" onClick={analyze} disabled={loading} className="mt-6 bg-[#F97316] hover:bg-[#EA580C] rounded-full h-11 px-6">
              {loading ? t(lang, "analyzing") : t(lang, "analyze_btn")} <Sparkles className="w-4 h-4 ml-2" />
            </Button>
            {user?.diploma && (
              <p className="text-xs text-white/60 mt-3">
                {t(lang, "profile_label")}: <b>{user.diploma}</b>
                {user.specialty && ` · ${user.specialty}`}
                {user.home_country && ` · 📍 ${user.home_region ? `${user.home_region}, ` : ""}${user.home_country}`}
              </p>
            )}
          </div>
        </div>

        {result && (
          <div className="mt-10 grid md:grid-cols-2 gap-6 fade-up">
            <div className="rounded-2xl bg-white border border-slate-200 p-6">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">{t(lang, "matched_sectors")}</p>
              <div className="space-y-4">
                {result.suggested_sectors?.map((s, i) => (
                  <div key={i} data-testid={`sector-${i}`}>
                    <div className="flex justify-between items-baseline mb-1.5">
                      <span className="font-semibold text-slate-900">{s.sector}</span>
                      <span className="text-sm text-[#C2410C] font-bold">{s.score}%</span>
                    </div>
                    <Progress value={s.score} className="h-2" />
                    <p className="text-xs text-slate-600 mt-1.5">{s.reasoning}</p>
                  </div>
                ))}
              </div>
              {result.overall_advice && (
                <div className="mt-6 p-4 rounded-xl bg-[#FFF7ED] border border-[#F97316]/30">
                  <p className="text-sm text-[#C2410C] leading-relaxed">{result.overall_advice}</p>
                </div>
              )}
              {result.suggested_roles?.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">{t(lang, "suggested_roles")}</p>
                  <div className="flex flex-wrap gap-2">
                    {result.suggested_roles.map((r) => <Badge key={`role-${r}`} variant="secondary" className="rounded-full">{r}</Badge>)}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-2xl bg-white border border-slate-200 p-6">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">{t(lang, "recommended_companies")}</p>
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {result.matching_companies?.map((c) => (
                  <div key={c.id} data-testid={`match-co-${c.id}`} className="rounded-xl border border-slate-200 p-4 hover:-translate-y-0.5 hover:shadow-md transition-transform">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="font-semibold">{c.name}</p>
                        <p className="text-xs text-slate-500">{c.sector} · {c.city}, {c.country}</p>
                        <p className="text-xs text-slate-600 mt-1 line-clamp-2">{c.description}</p>
                      </div>
                      <span className="text-lg font-display font-bold text-[#C2410C]">{c.match_score}%</span>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" onClick={() => setApplyCo(c)} className="bg-[#F97316] hover:bg-[#EA580C] rounded-full h-8">
                        {t(lang, "apply_spontaneous")}
                      </Button>
                    </div>
                  </div>
                ))}
                {!result.matching_companies?.length && (
                  <div className="text-center text-sm text-slate-500 py-6"><Building2 className="w-6 h-6 mx-auto mb-2 opacity-40" />—</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <ApplyDialog open={!!applyCo} onOpenChange={(v) => !v && setApplyCo(null)} company={applyCo} />
    </Layout>
  );
}
